import { useId, useState } from 'react';
import type { SaveCelebrationEmailTemplate } from '@om/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { guestService, type InvitationEmailPreviewResponse } from '@/lib/api/guest.service';
import { handleApiError } from '@/lib/api/client';
import { DEFAULT_THEME } from '@/lib/landing-theme-constants';
import { INVITATION_EMAIL_PRESETS } from '@/lib/invitationEmailPresets';
import type { LandingThemeConfig } from '@/types/celebration.types';

export const newInvitationTemplate = (): SaveCelebrationEmailTemplate => {
  const { subject, intro, ctaLabel, footer } = INVITATION_EMAIL_PRESETS.warm;
  return { name: '', subject, intro, ctaLabel, footer };
};

export function templateTheme(content: SaveCelebrationEmailTemplate, base?: LandingThemeConfig): LandingThemeConfig {
  return {
    ...DEFAULT_THEME, ...base,
    invitation_email_subject: content.subject,
    invitation_email_intro: content.intro,
    invitation_email_cta_label: content.ctaLabel,
    invitation_email_footer: content.footer,
  };
}

export function InvitationTemplateEditor({ celebrationId, value, onChange, onSave, onCancel, canManage, baseTheme }: {
  celebrationId: number; value: SaveCelebrationEmailTemplate;
  onChange: (value: SaveCelebrationEmailTemplate) => void;
  onSave: (value: SaveCelebrationEmailTemplate) => Promise<void>;
  onCancel: () => void; canManage: boolean; baseTheme?: LandingThemeConfig;
}) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [recipient, setRecipient] = useState('');
  const [preview, setPreview] = useState<InvitationEmailPreviewResponse | null>(null);
  async function run(action: () => Promise<void>) {
    setBusy(true); setError(''); setNotice('');
    try { await action(); } catch (err) { setError(handleApiError(err)); } finally { setBusy(false); }
  }
  return <section aria-label="Template editor" className="space-y-4 rounded-xl border bg-muted/20 p-4">
    <p className="text-sm text-muted-foreground">Save a reusable email template. Each batch keeps its own content snapshot; later edits do not change existing batches.</p>
    <fieldset disabled={!canManage || busy} className="grid gap-4 sm:grid-cols-2">
      {(['name', 'subject', 'intro', 'ctaLabel', 'footer'] as const).map(field => {
        const label = { name: 'Template name', subject: 'Subject', intro: 'Introduction', ctaLabel: 'Button label', footer: 'Footer' }[field];
        return <div key={field} className={field === 'intro' || field === 'footer' ? 'space-y-1 sm:col-span-2' : 'space-y-1'}>
          <Label htmlFor={`${id}-${field}`}>{label}</Label>
          {field === 'intro' || field === 'footer'
            ? <Textarea id={`${id}-${field}`} value={value[field]} onChange={event => { onChange({ ...value, [field]: event.target.value }); setPreview(null); }} />
            : <Input id={`${id}-${field}`} value={value[field]} onChange={event => { onChange({ ...value, [field]: event.target.value }); setPreview(null); }} />}
        </div>;
      })}
    </fieldset>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {notice && <p role="status" className="text-sm text-primary">{notice}</p>}
    <div className="flex flex-wrap items-end gap-2">
      <Button type="button" variant="outline" disabled={busy} onClick={() => void run(async () => {
        setPreview(await guestService.getInvitationEmailPreview(celebrationId, templateTheme(value, baseTheme)));
      })}>Preview email</Button>
      {canManage && <>
        <div className="space-y-1"><Label htmlFor={`${id}-test`}>Test recipient email</Label>
          <Input id={`${id}-test`} type="email" value={recipient} disabled={busy} onChange={event => setRecipient(event.target.value)} />
        </div>
        <Button type="button" variant="outline" disabled={busy || !recipient.trim()} onClick={() => void run(async () => {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim())) throw new Error('Enter a valid test email address.');
          const result = await guestService.sendTestInvitationEmail(celebrationId, recipient.trim(), templateTheme(value, baseTheme));
          setNotice(`Test email submitted for ${result.recipient}.`);
        })}>Send test email</Button>
        <Button type="button" disabled={busy} onClick={() => void run(async () => {
          if (!value.name.trim() || !value.subject.trim() || !value.intro.trim() || !value.ctaLabel.trim()) {
            throw new Error('Template name, subject, introduction and button label are required.');
          }
          await onSave({ ...value, name: value.name.trim() });
        })}>{busy ? 'Working…' : 'Save template'}</Button>
      </>}
      <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>Close editor</Button>
    </div>
    {preview && <div className="overflow-hidden rounded-lg border bg-white">
      <p className="border-b bg-muted p-3 text-sm text-foreground">Subject: {preview.subject}</p>
      <iframe title="Invitation email preview" sandbox="" srcDoc={preview.html} className="h-[480px] w-full" />
    </div>}
  </section>;
}